import datacentral from '@/utils/axios/datacentral'
import qs from 'query-string'
import {
    BusinessPerformanceReport,
    BusinessPerformanceParams,
    BusinessPerformanceResponse,
    ProfitabilityRatioItem,
    ProfitabilityRatiosParams,
    ProfitabilityRatiosResponse,
    FinancialStrengthRatio,
    FinancialStrengthRatiosParams,
    FinancialStrengthRatiosResponse,
    FinancialStrengthRatioCreateParams,
    FinancialStrengthRatioDeleteParams,
    ManagementEfficiencyRatio,
    ManagementEfficiencyRatiosParams,
    ManagementEfficiencyRatiosResponse,
    ManagementEfficiencyRatioCreateParams,
    ManagementEfficiencyRatioDeleteParams,
    OperatingCapabilityRatio,
    OperatingCapabilityRatiosParams,
    OperatingCapabilityRatiosResponse,
    OperatingCapabilityRatioCreateParams,
    OperatingCapabilityRatioDeleteParams,
    BalanceSheetSummary,
    BalanceSheetSummaryParams,
    BalanceSheetSummaryResponse,
    BalanceSheetMonthlySummaryParams,
    BalanceSheetMonthlySummaryResponse,
    BatchBalanceSheetCreateParams,
    BatchBalanceSheetCreateResponse,
    BalanceSheetListParams,
    BalanceSheetListResponse,
    BalanceSheetItem,
} from '../types'

const BASE_URL = '/reports/bi/revenue-reports/business-performance'
const PROFITABILITY_URL = '/reports/bi/revenue-reports/profitability-ratios'
const FINANCIAL_STRENGTH_URL = '/reports/bi/financial-ratios/strength/list'
const FINANCIAL_STRENGTH_CRUD_URL = '/reports/bi/financial-ratios/strength'
const MANAGEMENT_EFFICIENCY_URL = '/reports/bi/financial-ratios/efficiency/list'
const MANAGEMENT_EFFICIENCY_CRUD_URL = '/reports/bi/financial-ratios/efficiency'
const OPERATING_CAPABILITY_URL = '/reports/bi/financial-ratios/capability/list'
const OPERATING_CAPABILITY_CRUD_URL = '/reports/bi/financial-ratios/capability'
const BALANCE_SHEET_SUMMARY_URL = '/reports/bi/balance-sheet/summary'
const BALANCE_SHEET_MONTHLY_URL = '/reports/bi/balance-sheet/monthly-summary'
const BALANCE_SHEET_URL = '/reports/bi/balance-sheet'
const BALANCE_SHEET_BATCH_URL = '/reports/bi/balance-sheet/batch'

/**
 * Get Business Performance P&L Report
 * @param params - Query parameters for filtering
 * @returns Promise with business performance reports
 */
export const getBusinessPerformance = async (
    params?: BusinessPerformanceParams
): Promise<BusinessPerformanceReport[]> => {
    const queryString = qs.stringify(params || {})
    const { data } = await datacentral.get<BusinessPerformanceResponse>(
        `${BASE_URL}?${queryString}`
    )
    return data?.results || []
}

/**
 * Get Profitability Ratios
 * @param params - Query parameters with from_month, to_month, and optional tax_rate
 * @returns Promise with profitability ratio items
 */
export const getProfitabilityRatios = async (
    params: ProfitabilityRatiosParams
): Promise<ProfitabilityRatioItem[]> => {
    const queryString = qs.stringify(params)
    const { data } = await datacentral.get<ProfitabilityRatiosResponse>(
        `${PROFITABILITY_URL}?${queryString}`
    )
    return data?.results || []
}

/**
 * Get Financial Strength Ratios
 * @param params - Query parameters with optional from_month and to_month
 * @returns Promise with financial strength ratio items
 */
export const getFinancialStrengthRatios = async (
    params?: FinancialStrengthRatiosParams
): Promise<FinancialStrengthRatio[]> => {
    const queryString = qs.stringify(params || {})
    const { data } = await datacentral.get<FinancialStrengthRatiosResponse>(
        `${FINANCIAL_STRENGTH_URL}?${queryString}`
    )
    return data?.results || []
}

/**
 * Get Management Efficiency Ratios
 * @param params - Query parameters with optional from_month and to_month
 * @returns Promise with management efficiency ratio items
 */
export const getManagementEfficiencyRatios = async (
    params?: ManagementEfficiencyRatiosParams
): Promise<ManagementEfficiencyRatio[]> => {
    const queryString = qs.stringify(params || {})
    const { data } = await datacentral.get<ManagementEfficiencyRatiosResponse>(
        `${MANAGEMENT_EFFICIENCY_URL}?${queryString}`
    )
    return data?.results || []
}

/**
 * Get Operating Capability Ratios
 * @param params - Query parameters with optional from_month and to_month
 * @returns Promise with operating capability ratio items
 */
export const getOperatingCapabilityRatios = async (
    params?: OperatingCapabilityRatiosParams
): Promise<OperatingCapabilityRatio[]> => {
    const queryString = qs.stringify(params || {})
    const { data } = await datacentral.get<OperatingCapabilityRatiosResponse>(
        `${OPERATING_CAPABILITY_URL}?${queryString}`
    )
    return data?.results || []
}

/**
 * Get Balance Sheet Summary
 * @param params - Query parameters with report_month or from_month/to_month range
 * @returns Promise with balance sheet summary items
 */
export const getBalanceSheetSummary = async (
    params: BalanceSheetSummaryParams
): Promise<BalanceSheetSummary[]> => {
    const queryString = qs.stringify(params)
    const { data } = await datacentral.get<BalanceSheetSummaryResponse>(
        `${BALANCE_SHEET_SUMMARY_URL}?${queryString}`
    )
    return data?.results || []
}

/**
 * Get Balance Sheet Monthly Summary (Time-series)
 * @param params - Query parameters with required from_month and to_month
 * @returns Promise with balance sheet monthly summary items
 */
export const getBalanceSheetMonthlySummary = async (
    params: BalanceSheetMonthlySummaryParams
): Promise<BalanceSheetSummary[]> => {
    const queryString = qs.stringify(params)
    const { data } = await datacentral.get<BalanceSheetMonthlySummaryResponse>(
        `${BALANCE_SHEET_MONTHLY_URL}?${queryString}`
    )
    return data?.results || []
}

// ===== CRUD Operations for Financial Ratios =====

/**
 * Create or Update Financial Strength Ratio (Upsert)
 * @param params - Ratio data for a specific month
 * @returns Promise with created/updated ratio
 */
export const createFinancialStrengthRatio = async (
    params: FinancialStrengthRatioCreateParams
): Promise<FinancialStrengthRatio> => {
    const { data } = await datacentral.post<{
        message: string
        result: FinancialStrengthRatio
    }>(FINANCIAL_STRENGTH_CRUD_URL, params)
    return data.result
}

/**
 * Delete Financial Strength Ratio
 * @param params - Month to delete
 * @returns Promise
 */
export const deleteFinancialStrengthRatio = async (
    params: FinancialStrengthRatioDeleteParams
): Promise<void> => {
    const queryString = qs.stringify(params)
    await datacentral.delete(`${FINANCIAL_STRENGTH_CRUD_URL}?${queryString}`)
}

/**
 * Create or Update Management Efficiency Ratio (Upsert)
 * @param params - Ratio data for a specific month
 * @returns Promise with created/updated ratio
 */
export const createManagementEfficiencyRatio = async (
    params: ManagementEfficiencyRatioCreateParams
): Promise<ManagementEfficiencyRatio> => {
    const { data } = await datacentral.post<{
        message: string
        result: ManagementEfficiencyRatio
    }>(MANAGEMENT_EFFICIENCY_CRUD_URL, params)
    return data.result
}

/**
 * Delete Management Efficiency Ratio
 * @param params - Month to delete
 * @returns Promise
 */
export const deleteManagementEfficiencyRatio = async (
    params: ManagementEfficiencyRatioDeleteParams
): Promise<void> => {
    const queryString = qs.stringify(params)
    await datacentral.delete(`${MANAGEMENT_EFFICIENCY_CRUD_URL}?${queryString}`)
}

/**
 * Create or Update Operating Capability Ratio (Upsert)
 * @param params - Ratio data for a specific month
 * @returns Promise with created/updated ratio
 */
export const createOperatingCapabilityRatio = async (
    params: OperatingCapabilityRatioCreateParams
): Promise<OperatingCapabilityRatio> => {
    const { data } = await datacentral.post<{
        message: string
        result: OperatingCapabilityRatio
    }>(OPERATING_CAPABILITY_CRUD_URL, params)
    return data.result
}

/**
 * Delete Operating Capability Ratio
 * @param params - Month to delete
 * @returns Promise
 */
export const deleteOperatingCapabilityRatio = async (
    params: OperatingCapabilityRatioDeleteParams
): Promise<void> => {
    const queryString = qs.stringify(params)
    await datacentral.delete(`${OPERATING_CAPABILITY_CRUD_URL}?${queryString}`)
}

/**
 * Batch Create or Update Balance Sheet Items (Upsert)
 * @param params - Balance sheet items data for a specific month
 * @returns Promise with created/updated items
 */
export const batchCreateBalanceSheetItems = async (
    params: BatchBalanceSheetCreateParams
): Promise<BalanceSheetItem[]> => {
    const { data } = await datacentral.post<BatchBalanceSheetCreateResponse>(
        BALANCE_SHEET_BATCH_URL,
        params
    )
    return data.results
}

/**
 * List Balance Sheet Items
 * @param params - Query parameters for filtering items
 * @returns Promise with balance sheet items
 */
export const listBalanceSheetItems = async (
    params?: BalanceSheetListParams
): Promise<BalanceSheetItem[]> => {
    const queryString = qs.stringify(params || {})
    const { data } = await datacentral.get<BalanceSheetListResponse>(
        `${BALANCE_SHEET_URL}?${queryString}`
    )
    return data.results || []
}
